import axios from "axios";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { getAccessToken } from "./authUtils";

//const apiUrl = "https://aswa-tccc-bi-java-dev-evfucnf8bzd2dqbb.eastus2-01.azurewebsites.net/api";
const apiUrl = process.env.REACT_APP_BACKEND_BASE_URL;

// Store MSAL instance globally
let msalInstance = null;

// Function to set MSAL instance (called from App.js)
export const setMsalInstance = (instance) => {
  msalInstance = instance;
};

const axiosInstance = axios.create({
  baseURL: apiUrl
});

let toastShown = false; // Flag to track if toast has been shown

export const resetToastShown = () => {
  toastShown = false;
};

axiosInstance.interceptors.request.use(
  async (config) => {
    if (!msalInstance) throw new Error("MSAL instance not initialized");
    const token = await getAccessToken(msalInstance);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }
    throw new Error("Failed to acquire access token");
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // Reset the flag on successful response
    toastShown = false;
    return response;
  },
  (error) => {
    toastShown = false;
    if (error.response) {

      if (!toastShown) {
        switch (error.response.status) {
          case 400:
            console.error("Bad Request");
            toast.error("Bad Request: The server could not understand the request due to invalid syntax.");
            break;
          //need to throw to login
          case 401:
            console.error("Unauthorized access - perhaps redirect to login?");

            // localStorage.clear();
            // msalInstance?.logoutRedirect();

            toast.error("Unauthorized: Access is denied due to invalid credentials. Please log in.");
            break;
          case 403:
            console.error("Forbidden");

            localStorage.clear();
            msalInstance?.logoutRedirect();

            toast.error("Forbidden: You do not have permission to access this resource.");
            break;
          case 404:
            console.error("Resource Not Found");
            toast.error("Not Found: The requested resource could not be found on the server.");
            break;
          case 500:
            console.error("Internal Server Error");
            toast.error("Internal Server Error: The server encountered an error and could not complete your request.");
            break;
          default:
            console.error(`Unhandled error status: ${error.response.status}`);
            toast.error(`Error: An unexpected error occurred (status code: ${error.response.status}).`);
        }
        toastShown = true; // Set the flag to true after showing the toast
      }
    } else if (error.request) {
      if (!toastShown) {
        console.error("No response received:", error.request);
        toast.error("Network Error: No response received from the server.");
        toastShown = true; // Set the flag to true after showing the toast
      }
    } else {
      if (!toastShown) {
        console.error("Error:", error.message);
        toast.error(`Error: ${error.message}`);
        toastShown = true; // Set the flag to true after showing the toast
      }
    }

    return error.response;
  }
);

const ApiService = {
  get(endpoint, params) {
    return axiosInstance.get(endpoint, params);
  },

  post(endpoint, data, headers = {}) {
    // Check if data is FormData and set Content-Type accordingly
    const isMultipart = data instanceof FormData;
    const config = {
      headers: {
        ...(isMultipart ? {} : { "Content-Type": "application/json" }),
        ...headers,
      },
    };
    return axiosInstance.post(endpoint, data, config);
  },

  downloadFile(endpoint, data, headers = {}, params = {}) {
    // Check if data is FormData and set Content-Type accordingly
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      responseType: 'blob',
      params: params
    };
    return axiosInstance.post(endpoint, data, config);
  },

  uploadAndDownload(endpoint, data, headers = {}, params = {}) {
    // Check if data is FormData and set Content-Type accordingly
    const isMultipart = data instanceof FormData;
    const config = {
      headers: {
        "Content-Type": isMultipart ? "multipart/form-data" : "application/json",
        ...headers,
      },
      responseType: 'blob',
      params: params
    };
    return axiosInstance.post(endpoint, data, config);
  },

  put(endpoint, data) {
    return axiosInstance.put(endpoint, data);
  },

  patch(endpoint, data) {
    return axiosInstance.patch(endpoint, data);
  },

  delete(endpoint, data) {
    return axiosInstance.delete(endpoint, { data });
  },

};

export default ApiService;